import {observer} from "mobx-react";
import AHref from "../../../components/ahref/ahref";
import Currency from "../../../components/currency/currency";
import FontBody3 from "../../../components/fonts/font-body3/font-body3";
import {FONT_COLOR, FONT_VARIANT} from "../../../components/fonts/font-props";
import IconCoins from "../../../components/icons/icon-coins/icon-coins";
import IconDollarPage from "../../../components/icons/icon-dollar-page/icon-dollar-page";
import IconUser from "../../../components/icons/icon-user/icon-user";
import Icon from "../../../components/icons/icon/icon";
import DataCard from "../../../components/layouts/data-card/data-card";
import DataCardGridLayout from "../../../components/layouts/data-card/data-card-layouts/data-card-grid-layout";
import ExternalLink from "../../../pages/manage-returns/components/external-link/external-link";
import useDashboardTranslation from "../../../dashboard-translation/hooks/dashboard-translation-hook";
import CustomerStore from "../stores/customer-store";
import './customer-data-card.scss'
import RmasLinkableList from "./rmas-linkable-list";
import CustomerCaseStore from "../../manage-returns/rma-page/stores/customer-case-store";
import {Tooltip} from "../../../components/tooltip/tooltip";
import currentPortalStore from "../../../stores/current-portal-store";
import {FEATURES} from "../../../models/Plan";
import RmaShopperDetailsStore from "../../manage-returns/rma-page/stores/rma-shopper-details-store";
import CustomerDataCardShippingSection from "./customer-data-card-shipping-section";
import CustomerDataCardShopperName from "./customer-data-card-shopper-name";

interface ICustomerNameProps {
    customerStore: CustomerStore;
    noData: string;
    colorVarName?: string;
    tooltip?: string;
    shopperDetails?: RmaShopperDetailsStore;
}
const CustomerName: React.FC<ICustomerNameProps> = observer(({
    customerStore,
    noData,
    colorVarName,
    tooltip,
    shopperDetails,
}) => {
    if (shopperDetails) {
        return (
            <CustomerDataCardShopperName
                customerStore={customerStore}
                shopperDetails={shopperDetails}
                noData={noData}
                colorVarName={colorVarName}
                tooltip={tooltip}
            />
        );
    }

    colorVarName = colorVarName ?? 'color-semantic-3';

    return (
        <div className="customer-data-card-customer-name">
            <div className="customer-data-card-customer-name-content">
                <Tooltip content={tooltip}>
                    <Icon Component={IconUser} color={`var(--${colorVarName})`}/>
                </Tooltip>
                {!customerStore.isLoading && <ExternalLink label={customerStore?.fullName ?? noData} url={customerStore?.customerURL} />}
            </div>
        </div>
    );
});



interface IProps{
    customer?: CustomerStore;
    customerCase?: CustomerCaseStore;
    orderShippingAddress?: string;
    conversionRate?: number;
    currencyCode: string;
    hasTopIndicator?: boolean,
    TopIndicator?: React.FC,
    addressId?: string|null,
    isGift?: boolean,
    shopperDetails?: RmaShopperDetailsStore,
}
const CustomerDataCard: React.FC<IProps> = observer(({
    customer,
    customerCase,
    orderShippingAddress,
    conversionRate,
    currencyCode,
    hasTopIndicator,
    TopIndicator,
    addressId,
    isGift,
    shopperDetails,
}) => {

    const { getTranslation } = useDashboardTranslation();
    const pageScope = 'dashboard-create-rma';
    const rmaScoreColorMap : { [key: string]: string } = {
        Green:    'color-green-score',
        Yellow:   'color-yellow-score',
        Red:      'color-red-score',
    }

    if (!customer) {
        return (
            <div className="customer-data-card">
                <DataCard hasTopIndicator={hasTopIndicator}><></></DataCard>
            </div>
        );
    }
    const scoreColor = currentPortalStore.isFeatureAllowed(FEATURES.BETA_SHOW_RMA_SCORE_ON_RETURNS_PAGE) && customerCase?.rmaScore ?
        rmaScoreColorMap[customerCase?.rmaScore] :
        undefined;
    const scoreReason = scoreColor ? customerCase?.rmaScoreReason ?? getTranslation('NO_DATA', pageScope) : undefined;

    return (
        <div className="customer-data-card">
            <DataCard
            hasTopIndicator={hasTopIndicator}
            TopIndicator={TopIndicator}
            toolbar={{
                content: <CustomerName
                    customerStore={customer}
                    noData={getTranslation('NO_DATA', pageScope)}
                    colorVarName={scoreColor}
                    tooltip={scoreReason}
                    shopperDetails={shopperDetails}
                />,
            }}>
                {(customer.isNotFound || customer.isNotExists) && (
                  <div className={'customer-data-unavailable-overlay'}>
                      <div className={'customer-data-unavailable-badge'}>
                          {getTranslation('NO_CUSTOMER_INFORMATION', pageScope)}
                      </div>
                  </div>
                )}
                <div className={'customer-data-card-content'}>
                    <DataCardGridLayout>
                        <FontBody3 color={FONT_COLOR.PRIMARY2}>{getTranslation('RMAS', pageScope)} </FontBody3>
                        <FontBody3 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>
                            <RmasLinkableList 
                                isLoading={customer.isLoadingRmas || customer.isLoading}
                                rmasList={customer.getRmas()}
                                noData={getTranslation("DATA_VALUE_NOT_APPLICABLE", "dashboard-returns-rma-card")}
                                searchInput={customer.denormalizedId}
                            />
                        </FontBody3>
                        <FontBody3 color={FONT_COLOR.PRIMARY2}>{getTranslation('STORE_CREDIT', pageScope)}</FontBody3>
                        <FontBody3 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>
                            <span className="return-credits-links">
                                <AHref href={customer?.getBalanceLink() ?? ''} target="_blank">
                                    <Icon Component={IconCoins}></Icon><Currency currencyCode={currencyCode} amount={customer?.getCreditBalance(conversionRate) ?? 0}/></AHref>
                                <AHref href={customer?.getTransactionsLink() ?? ''} target="_blank"><Icon Component={IconDollarPage}></Icon>{getTranslation('TRANSACTIONS', pageScope)}</AHref>
                            </span>
                        </FontBody3>
                        <CustomerDataCardShippingSection
                            orderShippingAddress={orderShippingAddress}
                            customerCase={customerCase}
                            addressId={addressId}
                            isGift={isGift}
                            shopperDetails={shopperDetails}
                        />
                    </DataCardGridLayout>
                </div>
            </DataCard>
        </div>
    );
});

export default CustomerDataCard;
